import Link from 'next/link';
import { CSSProperties, MouseEventHandler } from 'react';
import { useReduxState } from 'hooks';
import { FlagIcon, Tooltip } from 'components/common';
import { Icon } from 'components/common/icons';

import type { JSX, Ref } from 'react';
import type { ButtonInterface } from 'components/common/Button';
import type { IconProp } from 'components/common/icons/Icon';

export interface DropdownButtonInterface extends ButtonInterface {
  flag?: string;
  'data-keep-open'?: boolean;
  hideBreakpointGreaterThan?: number;
  hideBreakpointLessThan?: number;
  href?: string;
  icon?: IconProp;
  iconContent?: React.ReactNode;
  isSelectable?: boolean;
  isSelected?: boolean;
  loading?: boolean;
  onClick?: (
    event?: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ...params
  ) => void;
  ref?: Ref<HTMLButtonElement> | Ref<HTMLAnchorElement>;
  showNotification?: boolean;
  style?: CSSProperties;
  subText?: string;
  text?: string | JSX.Element;
  textMinWidth?: string;
  tooltip?: string;
  unreadItems?: number;
  unreadItemsTooltip?: string;
  warningItems?: number;
  whiteSpace?: string;
}

const DropdownButton = ({
  flag,
  hideBreakpointGreaterThan = 0,
  hideBreakpointLessThan = 0,
  href,
  icon,
  iconContent,
  isSelectable,
  isSelected,
  loading,
  onClick,
  ref,
  showNotification,
  style,
  subText = '',
  text,
  textMinWidth = '100px',
  tooltip,
  unreadItems,
  unreadItemsTooltip,
  warningItems,
  whiteSpace = 'nowrap',
  ...props
}: DropdownButtonInterface) => {
  const { theme } = useReduxState();

  const ButtonElement = href ? Link : 'button';

  return (
    <ButtonElement
      className={`button ${isSelected ? 'is-selected' : ''} ${
        hideBreakpointGreaterThan ? 'with-hide-breakpoint-greater-than' : ''
      } ${hideBreakpointLessThan ? 'with-hide-breakpoint-less-than' : ''}`}
      href={href}
      onClick={onClick as MouseEventHandler}
      // @ts-ignore
      ref={ref}
      style={style}
      {...(isSelectable ? { role: 'menuitemradio' } : {})}
      {...(isSelected ? { 'aria-checked': true } : { 'aria-checked': false })}
      {...(tooltip ? { 'aria-label': tooltip, 'data-tooltip': tooltip } : {})}
      {...props}
    >
      <div className="content-wrapper">
        {flag ? (
          <div className="icon">
            <FlagIcon lang={flag} size="small" style={{ marginTop: -2 }} />
          </div>
        ) : icon || iconContent ? (
          <div className="icon">
            {icon ? <Icon icon={icon} fixedWidth={true} /> : iconContent}
          </div>
        ) : null}

        {text ? (
          <div className="text">
            <div className="text-wrapper">
              <div className="text-label">{text}</div>
              {subText ? <div className="subtext">{subText}</div> : null}
            </div>

            {loading ? <span className="loading" /> : null}

            {unreadItems || warningItems ? (
              <Tooltip
                childrenWrapInDiv={false}
                content={unreadItemsTooltip}
                usePortal={true}
              >
                <div className={unreadItems ? 'unread-items' : 'warning-items'}>
                  {unreadItems || warningItems}
                </div>
              </Tooltip>
            ) : null}
          </div>
        ) : null}
      </div>

      {isSelected ? (
        <div className="selected">
          <Icon icon="check" />
        </div>
      ) : null}

      {showNotification ? <div className="notification" /> : null}

      <style jsx>{`
        .button {
          background-color: white;
          border: 0;
          border-radius: ${theme.buttonBorderRadius};
          color: ${theme.textColorLight};
          cursor: pointer;
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: space-between;
          font-family: inherit;
          font-size: 1.4rem;
          font-weight: 600;
          height: auto;
          margin: 0;
          max-width: 300px;
          outline: 0;
          padding: 8px 12px;
          position: relative;
          text-align: left;
          text-decoration: none;
          transition: background-color 0.3s, color 0.3s;
          white-space: ${whiteSpace};
          width: 100%;
        }
        .content-wrapper {
          display: flex;
          flex-direction: row;
          align-items: center;
          min-width: 0;
          flex: 1 1 auto;
        }
        .button:focus,
        .button:hover {
          background-color: ${theme.lightGreyLighter};
        }
        .button:disabled,
        .button:disabled:hover {
          background-color: white;
          cursor: default;
          opacity: 0.5;
        }
        .button.is-selected {
          color: ${theme.textColor};
          font-weight: 700;
        }
        .icon {
          color: ${theme.textColorLighter};
          flex-grow: 0;
          flex-shrink: 0;
          margin-right: 6px;
        }
        .text-wrapper {
          flex-grow: 1;
        }
        .text-label {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: normal;
        }
        .text {
          display: flex;
          flex-grow: 1;
          flex-shrink: 1;
          margin-left: 3px;
          min-width: ${textMinWidth};
          align-items: center;
        }
        .subtext {
          color: ${theme.textColorLighter};
          font-size: 1.2rem;
          font-weight: 600;
          line-height: 1.3;
          padding: 2px 0;
        }
        .selected {
          color: ${theme.textColor};
          font-size: 1rem;
          margin-left: 10px;
          flex-shrink: 0;
          display: flex;
          align-items: center;
        }
        .notification {
          background-color: ${theme.dangerColor};
          border-radius: 10px;
          margin: 6px 2px 5px 12px;
          height: 6px;
          width: 6px;
        }
        .unread-items,
        .warning-items {
          border-radius: 20px;
          font-size: 1.3rem;
          font-weight: 700;
          line-height: 1;
          margin-left: 8px;
          padding: 2px 6px 3px;
          position: relative;
          top: -1px;
        }
        .unread-items {
          background-color: ${theme.dangerColor};
          color: ${theme.dangerTextColor};
        }
        .warning-items {
          background-color: ${theme.warningColor};
          color: ${theme.warningTextColor};
        }
        @keyframes loading {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        .loading {
          animation: loading 1.2s infinite linear;
          border-style: solid;
          border-radius: 14px;
          border-top-color: transparent;
          border-width: 2px;
          display: inline-block;
          height: 12px;
          margin-left: 8px;
          position: relative;
          top: 2px;
          width: 12px;
        }
        @media (min-width: ${hideBreakpointGreaterThan}px) {
          .button.with-hide-breakpoint-greater-than {
            display: none !important;
          }
        }
        @media (max-width: ${hideBreakpointLessThan}px) {
          .button.with-hide-breakpoint-less-than {
            display: none !important;
          }
        }
      `}</style>
    </ButtonElement>
  );
};

export default DropdownButton;
